import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, FileText, Zap } from 'lucide-react';

const EPCFloorPlans = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "EPC Floor Plans", path: "/epc-floor-plans" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="epc-floor-plans"
        defaultTitle="EPC Floor Plans | House Plan Direct"
        defaultDescription="Floor plans for EPC assessors and Energy Performance Certificates. Compliant floor plans from £40. 24-48 hour delivery. Upload your sketch today."
        aiSummary="House Plan Direct provides floor plans suitable for EPC (Energy Performance Certificate) assessments across the UK. Our floor plans show clear room layouts and dimensions required for EPC calculations. Used by EPC assessors, landlords, and property sellers. Plans from £40."
        keywords="EPC floor plans, Energy Performance Certificate floor plan, floor plan for EPC, EPC assessor floor plan, EPC compliant floor plan UK"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-yellow-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Zap className="h-12 w-12 text-yellow-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            EPC Floor Plans
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Floor plans for Energy Performance Certificate assessments and EPC assessors.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> EPC Compliant
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Clock className="h-4 w-4 text-blue-600 mr-2" /> 24-48 hour delivery
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <FileText className="h-4 w-4 text-yellow-600 mr-2" /> From £40
            </span>
          </div>
          <Link to="/order">
            <Button size="lg" className="bg-yellow-600 hover:bg-yellow-700 text-white">
              Order Floor Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Already have a plan to check? <Link to="/measurement-tool" className="text-yellow-700 underline hover:text-yellow-800" data-testid="epc-link-measurement-tool">Measure it online for free</Link>, or draw one from scratch with the <Link to="/floor-plan-creator" className="text-yellow-700 underline hover:text-yellow-800" data-testid="epc-link-floor-plan-creator">Floor Plan Creator</Link>.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plans for EPC Assessments</h2>
          <p className="text-gray-600 mb-8">
            Energy Performance Certificates (EPCs) require accurate floor plans showing room layouts and dimensions. 
            Our floor plans are suitable for EPC assessments, providing clear property layouts that EPC assessors need 
            for their calculations.
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">When You Need an EPC Floor Plan</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Selling a property (EPC required for marketing)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Renting a property (EPC required for tenancies)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">New build completions</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Commercial property transactions</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-yellow-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Get Your EPC Floor Plan</h3>
            <p className="text-yellow-100 mb-6">Upload your sketch and receive your floor plan within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-yellow-600 hover:bg-gray-100">
                Order Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EPCFloorPlans;
